/**
 * ExportsManagerCard (#1853, epic #1855) - admin console view of the async
 * workspace export pipeline: every job queued, running, and finished, plus
 * the `_config` knobs the loader and worker read. Status only: no links, no
 * files. Manual refresh only; nothing polls.
 *
 * Reads GET /api/admin/exports (#1852), writes PUT /api/admin/exports/config
 * and POST /api/admin/exports/close, and refetches everything after each
 * write. History pages newest first; the page size is remembered in
 * localStorage and Load more appends the next page.
 *
 * Time handling follows the snapshot page: the backend stores windows in UTC
 * (whole-hour start, HH:MM end); the form shows and accepts local time in
 * two native time inputs (start stepped to whole hours) and converts at
 * Save. Job timestamps render in the browser's zone with its abbreviation
 * (#1867). Save is enabled only while the form differs from the loaded
 * config; Refresh lives in the Queued header and discards unsaved edits.
 * Close is per window (#1869): each saved row carries its own button,
 * enabled only while the clock is inside that stored window, and a dirty
 * or new row shows none until Save. The backend refuses a close outside
 * every window, so the two agree.
 *
 * Input bounds come from the list response's `limits.bounds` (the backend's
 * own table). The card refuses an out-of-bounds value before sending, and the
 * backend still validates on Save, so a 400 surfaces as an error toast.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconTrash } from '@tabler/icons-react';
import { apiGlobal } from '../../services/api';
import { useToast } from '../ToastNotification';
import { Badge, SectionContainer } from '../ds';

const errMessage = (err) =>
  err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

export const PAGE_SIZE_KEY = 'exportsHistoryPageSize';

// Fallback until the first load answers; the backend copy wins once it does.
const DEFAULT_LIMITS = {
  pageSizes: [50, 100, 200],
  bounds: {
    windows: { min: 1, max: 6 },
    maxConcurrent: { min: 1, max: 5 },
    fileRetentionDays: { min: 1, max: 30 },
    historyRetentionDays: { min: 1, max: 365 },
    rateLimitHours: { min: 0, max: 168 },
    linkMinutes: { min: 5, max: 60 },
  },
};

const KNOBS = [
  { key: 'maxConcurrent', label: 'Max concurrent' },
  { key: 'fileRetentionDays', label: 'File retention days' },
  { key: 'historyRetentionDays', label: 'History retention days' },
  { key: 'rateLimitHours', label: 'Rate limit hours' },
  { key: 'linkMinutes', label: 'Link minutes' },
];

const STATUS_VARIANT = { queued: 'neutral', running: 'interactive', done: 'current', failed: 'warning' };

const MINUTES_PER_DAY = 24 * 60;
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

// ----- Time helpers (same approach as Snapshots.jsx) ----------------------

// Date.getTimezoneOffset() returns minutes WEST of UTC (e.g. PST = 480).
const offsetMinutes = () => new Date().getTimezoneOffset();

const wrapHour = (h) => ((h % 24) + 24) % 24;
const wrapMinutes = (m) => ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

const utcHourToLocal = (utcHour) => wrapHour(Math.round(utcHour - offsetMinutes() / 60));
const localHourToUTC = (localHour) => wrapHour(Math.round(localHour + offsetMinutes() / 60));

const shiftHHMM = (hhmm, deltaMinutes) => {
  const [h, m] = hhmm.split(':').map(Number);
  const total = wrapMinutes(h * 60 + m + deltaMinutes);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};
const utcHHMMToLocal = (hhmm) => shiftHHMM(hhmm, -offsetMinutes());
const localHHMMToUTC = (hhmm) => shiftHHMM(hhmm, offsetMinutes());

const localTzAbbr = () => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZoneName: 'short' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
};

const hourToHHMM = (hour) => `${String(hour).padStart(2, '0')}:00`;

// Local wall time with the zone abbreviation, so a reader can tell the
// browser's zone from UTC (#1867). Job rows store ISO UTC instants.
const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const when = d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const tz = localTzAbbr();
  return tz ? `${when} ${tz}` : when;
};

const formatBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (ms) => (ms == null ? '' : `${(ms / 1000).toFixed(1)} s`);

// The stored window (UTC whole-hour start, HH:MM end) contains `date`;
// mirrors exportConfig.windowAt, overnight windows wrap past midnight.
const windowContains = (w, date) => {
  const now = date.getUTCHours() * 60 + date.getUTCMinutes();
  const start = w.startHourUtc * 60;
  const [eh, em] = w.endUtc.split(':').map(Number);
  const end = eh * 60 + em;
  return end > start ? now >= start && now < end : now >= start || now < end;
};

// ----- Form <-> config -----------------------------------------------------

const formFromConfig = (config) => ({
  windows: (config?.windows || []).map((w) => ({
    start: hourToHHMM(utcHourToLocal(w.startHourUtc)),
    end: utcHHMMToLocal(w.endUtc),
  })),
  ...Object.fromEntries(KNOBS.map(({ key }) => [key, String(config?.[key] ?? '')])),
});

const readPageSize = (sizes) => {
  try {
    const stored = Number(window.localStorage.getItem(PAGE_SIZE_KEY));
    if (sizes.includes(stored)) return stored;
  } catch {
    // storage unavailable: fall through to the default
  }
  return sizes[0];
};

const listPath = (limit, cursor) =>
  `/admin/exports?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;

// ----- Rows ----------------------------------------------------------------

// Queued rows lead with the next run (the list's `expectedStartUtc`, shared
// by every queued job) and then the request time (#1867).
const jobMeta = (job, nextRun) => {
  const parts = [job.requesterName || 'unknown requester'];
  if (job.status === 'queued') {
    if (nextRun) parts.push(`next run ${formatWhen(nextRun)}`);
    parts.push(`requested ${formatWhen(job.createdAt)}`);
  }
  else if (job.status === 'running') {
    parts.push(`started ${formatWhen(job.startedAt)}`);
    if (job.attempts > 1) parts.push(`attempt ${job.attempts}`);
  } else {
    parts.push(`finished ${formatWhen(job.finishedAt)}`);
    if (job.sizeBytes != null) parts.push(formatBytes(job.sizeBytes));
    if (job.durationMs != null) parts.push(formatDuration(job.durationMs));
  }
  return parts.filter(Boolean).join(' · ');
};

const JobRow = ({ job, nextRun }) => (
  <Box
    data-testid={`export-row-${job.jobId}`}
    sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, py: '6px' }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }} noWrap>
        {job.workspaceName || job.groupId}
      </Typography>
      <Typography
        sx={{ fontSize: '12px', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
        noWrap
      >
        {jobMeta(job, nextRun)}
      </Typography>
      {job.failureReason && (
        <Typography sx={{ fontSize: '12px', color: 'error.main' }} noWrap>
          {job.failureReason}
        </Typography>
      )}
    </Box>
    <Badge variant={STATUS_VARIANT[job.status] || 'neutral'}>{job.status}</Badge>
  </Box>
);

const EmptyLine = ({ children }) => (
  <Typography sx={{ color: 'text.dim', fontSize: 13, py: 1 }}>{children}</Typography>
);

// ----- Card ----------------------------------------------------------------

const ExportsManagerCard = () => {
  const { showSuccess, showError } = useToast();
  const [queued, setQueued] = useState([]);
  const [running, setRunning] = useState([]);
  const [history, setHistory] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [config, setConfig] = useState(null);
  const [nextRun, setNextRun] = useState(null);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [pageSize, setPageSize] = useState(() => readPageSize(DEFAULT_LIMITS.pageSizes));
  const [form, setForm] = useState(formFromConfig(null));
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const tz = localTzAbbr();

  // Full reload: replaces every section and resets the form to the saved
  // config, which discards unsaved edits (the explicit-refresh contract).
  const load = useCallback(async (limit) => {
    try {
      const res = await apiGlobal.get(listPath(limit));
      setQueued(res.queued || []);
      setRunning(res.running || []);
      setHistory(res.history?.items || []);
      setNextCursor(res.history?.nextCursor || null);
      setConfig(res.config || null);
      setNextRun(res.expectedStartUtc || null);
      setForm(formFromConfig(res.config));
      if (res.limits?.pageSizes?.length && res.limits?.bounds) setLimits(res.limits);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(pageSize); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setBusy(true);
    try { await load(pageSize); } finally { setBusy(false); }
  };

  // History-only reads: a page-size change restarts the list at page one and
  // Load more appends the next page. Neither touches config or the form, so
  // unsaved Settings edits survive.
  const fetchHistory = async (limit, cursor) => {
    setBusy(true);
    try {
      const res = await apiGlobal.get(listPath(limit, cursor));
      const items = res.history?.items || [];
      setHistory((h) => (cursor ? [...h, ...items] : items));
      setNextCursor(res.history?.nextCursor || null);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const cyclePageSize = async () => {
    const sizes = limits.pageSizes;
    const next = sizes[(sizes.indexOf(pageSize) + 1) % sizes.length];
    setPageSize(next);
    try { window.localStorage.setItem(PAGE_SIZE_KEY, String(next)); } catch { /* per-viewer convenience only */ }
    await fetchHistory(next);
  };

  const loadMore = () => fetchHistory(pageSize, nextCursor);

  const setKnob = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setWindow = (i, patch) => setForm((f) => ({
    ...f,
    windows: f.windows.map((w, j) => (j === i ? { ...w, ...patch } : w)),
  }));
  const addWindow = () => setForm((f) => ({ ...f, windows: [...f.windows, { start: '00:00', end: '02:00' }] }));
  const removeWindow = (i) => setForm((f) => ({ ...f, windows: f.windows.filter((_, j) => j !== i) }));

  // Client-side bounds check mirrors the backend's; overlap stays server-side.
  const buildPayload = () => {
    const { bounds } = limits;
    if (form.windows.length < bounds.windows.min || form.windows.length > bounds.windows.max) {
      throw new Error(`Windows must be ${bounds.windows.min} to ${bounds.windows.max} entries`);
    }
    const windows = form.windows.map((w, i) => {
      const start = HH_MM.exec(w.start);
      if (!start) throw new Error(`Window ${i + 1} needs a start time as HH:MM`);
      if (start[2] !== '00') throw new Error(`Window ${i + 1} start must be on the hour`);
      if (!HH_MM.test(w.end)) throw new Error(`Window ${i + 1} needs an end time as HH:MM`);
      return { startHourUtc: localHourToUTC(Number(start[1])), endUtc: localHHMMToUTC(w.end) };
    });
    const payload = { windows };
    for (const { key, label } of KNOBS) {
      const b = bounds[key];
      const n = Number(form[key]);
      if (form[key] === '' || !Number.isInteger(n) || n < b.min || n > b.max) {
        throw new Error(`${label} must be ${b.min} to ${b.max}`);
      }
      payload[key] = n;
    }
    return payload;
  };

  const save = async () => {
    let payload;
    try { payload = buildPayload(); } catch (err) { showError(err.message); return; }
    setBusy(true);
    try {
      await apiGlobal.put('/admin/exports/config', payload);
      showSuccess('Export settings saved');
      await load(pageSize);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const closeWindow = async () => {
    setBusy(true);
    try {
      await apiGlobal.post('/admin/exports/close', {});
      showSuccess('Closed until next window');
      setConfirmClose(false);
      await load(pageSize);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  const { bounds } = limits;
  // Save has something to write only while the form differs from what the
  // last load returned (#1867).
  const savedForm = formFromConfig(config);
  const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);
  // Per-window close (#1869): the button keys on the stored window at this
  // index, so a dirty or new row has none (null); otherwise whether the
  // clock is inside it and whether that open window is already closed.
  const closeStateFor = (i) => {
    const saved = config?.windows?.[i];
    if (!saved || JSON.stringify(form.windows[i]) !== JSON.stringify(savedForm.windows[i])) return null;
    const inside = windowContains(saved, new Date());
    return { inside, closed: inside && Boolean(config.closed) };
  };
  const knobField = ({ key, label }) => (
    <TextField
      key={key}
      size="small"
      type="number"
      label={label}
      value={form[key]}
      onChange={(e) => setKnob(key, e.target.value)}
      helperText={`${bounds[key].min} to ${bounds[key].max}`}
      slotProps={{ htmlInput: { min: bounds[key].min, max: bounds[key].max, step: 1 } }}
      sx={{ width: 180 }}
    />
  );

  return (
    <Stack spacing="13px">
      <SectionContainer
        title="Queued"
        category="neutral"
        count={queued.length}
        note={(
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={refresh}
            disabled={busy}
            sx={{ fontSize: '11px' }}
          >
            Refresh
          </Link>
        )}
      >
        <Stack sx={{ pt: '4px' }}>
          {queued.length === 0 && <EmptyLine>No exports queued.</EmptyLine>}
          {queued.map((job) => <JobRow key={job.jobId} job={job} nextRun={nextRun} />)}
        </Stack>
      </SectionContainer>

      <SectionContainer title="Running" category="interactive" count={running.length}>
        <Stack sx={{ pt: '4px' }}>
          {running.length === 0 && <EmptyLine>No exports running.</EmptyLine>}
          {running.map((job) => <JobRow key={job.jobId} job={job} />)}
        </Stack>
      </SectionContainer>

      <SectionContainer
        title="History"
        category="positive"
        count={history.length}
        note={(
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={cyclePageSize}
            disabled={busy}
            sx={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}
          >
            {`${pageSize} per page`}
          </Link>
        )}
      >
        <Stack sx={{ pt: '4px' }}>
          {history.length === 0 && <EmptyLine>No finished exports.</EmptyLine>}
          {history.map((job) => <JobRow key={job.jobId} job={job} />)}
          {nextCursor && (
            <Box sx={{ py: 1 }}>
              <Button size="small" onClick={loadMore} disabled={busy}>Load more</Button>
            </Box>
          )}
        </Stack>
      </SectionContainer>

      <SectionContainer title="Settings" category="budgets">
        <Stack spacing={2} sx={{ pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            {`Export windows (your local time${tz ? ', ' + tz : ''})`}
          </Typography>
          {form.windows.map((w, i) => {
            const close = closeStateFor(i);
            return (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }} alignItems={{ sm: 'center' }}>
              <TextField
                size="small"
                type="time"
                label={`Window ${i + 1} start`}
                value={w.start}
                onChange={(e) => setWindow(i, { start: e.target.value })}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 3600 } }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                type="time"
                label={`Window ${i + 1} end`}
                value={w.end}
                onChange={(e) => setWindow(i, { end: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 160 }}
              />
              {close && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  aria-label={`Close window ${i + 1} now`}
                  onClick={() => setConfirmClose(true)}
                  disabled={busy || !close.inside || close.closed}
                >
                  {close.closed ? 'Closed' : 'Close window now'}
                </Button>
              )}
              <IconButton
                size="small"
                aria-label={`remove window ${i + 1}`}
                disabled={busy || form.windows.length <= bounds.windows.min}
                onClick={() => removeWindow(i)}
                sx={{ color: 'text.secondary', p: '2px' }}
              >
                <IconTrash size={15} />
              </IconButton>
            </Stack>
            );
          })}
          <Box>
            <Button size="small" onClick={addWindow} disabled={busy || form.windows.length >= bounds.windows.max}>
              Add window
            </Button>
          </Box>

          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {KNOBS.map(knobField)}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Button variant="contained" size="small" onClick={save} disabled={busy || !dirty}>
              Save
            </Button>
          </Stack>
        </Stack>
      </SectionContainer>

      {/* Nothing is autofocused, so Enter right after open does nothing and
          Esc only cancels (same pattern as the Account page, #1801). */}
      <Dialog open={confirmClose} onClose={() => !busy && setConfirmClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Close the export window?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Workers stop claiming and chaining jobs within seconds. Queued
            exports wait for the next window; running ones finish.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)} disabled={busy}>Cancel</Button>
          <Button color="error" onClick={closeWindow} disabled={busy}>Close window</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ExportsManagerCard;
